import { useSelector } from 'react-redux';
import * as EncryptedKey from '../constants/encryptedRootReducerKeys';

export const useAuthSelector = () => {

	const authLoginResponse = useSelector((state) => state[EncryptedKey.authLogin]);

	const authPharmacy = authLoginResponse?.data?.data?.driver_pharmacy

	const isSupervisor = authLoginResponse?.data?.data?.driver_profile?.is_supervisor
	const authEmail = authLoginResponse?.data?.data?.email;
	// Driver and pharmacy-user IDs come from different tables and can have the
	// same number. Never use the driver's ID as a pharmacist identity.
	const topLevelPharmacistId = authLoginResponse?.data?.data?.driver_pharmacist_user?.id || null

	const driverDefaultPharmacist = authLoginResponse?.data?.data?.driver_profile?.driver_default_pharmacist

	const verifyDriverPasscodeResponse = useSelector((state) => state.verifyDriverPasscode);

	const authPharmacies = authLoginResponse?.data?.data?.driver_pharmacy

	const driverSelectedPharmacy = useSelector(state => state?.driverSelectedPharmacy?.data)

	const pharmacyId = driverSelectedPharmacy?.pharmacy_id

	const flexiUser = driverSelectedPharmacy?.is_flexi || false

	const hcpUser = driverSelectedPharmacy?.is_hcp ? driverSelectedPharmacy?.hcp_delivery : false

	const driverSelectedPharmacist = useSelector(state => state.driverSelectedPharmacist)
	const selectedPharmacistID = driverSelectedPharmacist?.data
	// console.log("driverSelectedPharmacist", driverSelectedPharmacist)
	// console.log("driverDefaultPharmacist", driverDefaultPharmacist)
	// console.log("selectedPharmacistID", selectedPharmacistID)

	const driverID = authLoginResponse?.data?.data?.driver_profile?.id

	const pharmacyDetail = useSelector((state) => state[EncryptedKey.pharmacyDetail])
	const pharmacyInspectionMode = pharmacyDetail?.data?.data?.inspection_mode

	const SOS_EmergencyEnabledResponse = useSelector(state => state.SOS_EmergencyEnabled)

	const driverOTPVerificationResponse = useSelector(state => state.driverOTPVerification)

	const normalizeRole = (role) => {
		const normalized = String(role || '').trim().toLowerCase();
		if (normalized === 'supervisor') return 'pharmacist';
		return normalized === 'pharmacist' || normalized === 'driver' ? normalized : null;
	}

	const getRoleFromPharmacyRecord = (record) => normalizeRole(
		record?.driver_pharmacist_user?.type ??
		record?.driver_profile?.type ??
		record?.type ??
		record?.role
	)

	const getMatchedPharmacyForSelectedPharmacy = () => {
		if (!pharmacyId || !driverDefaultPharmacist || !Array.isArray(driverDefaultPharmacist)) {
			return null;
		}

		return driverDefaultPharmacist.find(item => {
			const itemPharmacyId = item?.pharmacy_id;
			if (itemPharmacyId === pharmacyId) return true;
			if (String(itemPharmacyId) === String(pharmacyId)) return true;
			if (Number(itemPharmacyId) === Number(pharmacyId)) return true;
			return false;
		}) || null;
	}

	/**
	 * Get current user role for selected pharmacy
	 * IMPORTANT: A user can have different roles for different pharmacies:
	 * - They might be a 'driver' for Pharmacy A
	 * - They might be a 'pharmacist' for Pharmacy B
	 * This function returns the role for the currently selected pharmacy
	 * @returns {string|null} - 'pharmacist', 'driver', or null if not found
	 */
	const getCurrentUserRole = () => {
		const matchedPharmacy = getMatchedPharmacyForSelectedPharmacy();
		if (matchedPharmacy) {
			return getRoleFromPharmacyRecord(matchedPharmacy);
		}

		return (
			getRoleFromPharmacyRecord(driverSelectedPharmacy) ||
			normalizeRole(authLoginResponse?.data?.data?.driver_pharmacist_user?.type) ||
			normalizeRole(authLoginResponse?.data?.data?.driver_profile?.type) ||
			(Array.isArray(driverDefaultPharmacist) && driverDefaultPharmacist.length === 1
				? getRoleFromPharmacyRecord(driverDefaultPharmacist[0])
				: null)
		);
	};

	const currentUserRole = getCurrentUserRole();
	const isPharmacistForSelectedPharmacy = currentUserRole === 'pharmacist';
	const isDriverForSelectedPharmacy = currentUserRole === 'driver';

	// Get pharmacist ID for selected pharmacy from existing data structure (same method used in patientDetails.js)
	const getPharmacistIdForSelectedPharmacy = () => {
		const matchedPharmacy = getMatchedPharmacyForSelectedPharmacy();
		if (!matchedPharmacy) {
			return null;
		}
		
		// Return the ID from driver_pharmacist_user if it exists (regardless of type)
		return matchedPharmacy?.driver_pharmacist_user?.id || null;
	};

	const pharmacistIdForSelectedPharmacy = getPharmacistIdForSelectedPharmacy();
	const authPharmacistId = pharmacistIdForSelectedPharmacy || topLevelPharmacistId;
	const pharmacistNameForSelectedPharmacy =
		getMatchedPharmacyForSelectedPharmacy()?.driver_pharmacist_user?.name || null;

	return {
		authLoginResponse,
		verifyDriverPasscodeResponse,
		authEmail,
		authPharmacies,
		driverSelectedPharmacy,
		pharmacyId,
		flexiUser,
		driverSelectedPharmacist,
		selectedPharmacistID,
		driverID,
		pharmacyInspectionMode,
		SOS_EmergencyEnabledResponse,
		isSupervisor,
		authPharmacy,
		driverDefaultPharmacist,
		driverOTPVerificationResponse,
		authPharmacistId,
		currentUserRole,
		isPharmacistForSelectedPharmacy,
		isDriverForSelectedPharmacy,
		pharmacistIdForSelectedPharmacy,
		pharmacistNameForSelectedPharmacy,
		hcpUser
	}
}
